import { useEffect, useRef, useState, Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import Lenis from '@studio-freight/lenis';
import { Toaster } from "./components/ui/toaster";
import { SEO } from "./components/SEO";
import { CookieConsent } from "./components/CookieConsent";
import { faqData } from "./data/faqData";

// Components
import { Navigation } from "./components/Navigation";
import { HeroSection } from "./components/HeroSection";
import { AboutSection } from "./components/AboutSection";
import { ServicesSection } from "./components/ServicesSection";
import { MidPageCta } from "./components/MidPageCta";
import { ValuePropsSection } from "./components/ValuePropsSection";
import { SupportSection } from "./components/SupportSection";
import { TechStackSection } from "./components/TechStackSection";
import { TestimonialsSection } from "./components/TestimonialsSection";
import { FAQSection } from "./components/FAQSection";
import { ContactSection } from "./components/ContactSection";
import { Footer } from "./components/Footer";

// FAQPage JSON-LD generated directly from the same data that powers the
// visible <FAQSection>, so structured data always matches on-page content
// (Google's requirement for FAQ rich results, and generally good practice
// for AI answer engines that check for consistency).
const faqJsonLd = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "@id": "https://lolcorp.co.uk/#faq",
  mainEntity: faqData.map((item) => ({
    "@type": "Question",
    name: item.question,
    acceptedAnswer: {
      "@type": "Answer",
      text: item.answer,
    },
  })),
};

// Pages - code-split so homepage doesn't ship their JS upfront
const ConsultationPage = lazy(() =>
  import("./pages/ConsultationPage").then((m) => ({ default: m.ConsultationPage }))
);
const InstantQuotePage = lazy(() =>
  import("./pages/InstantQuotePage").then((m) => ({ default: m.InstantQuotePage }))
);
const MaintenancePage = lazy(() =>
  import("./pages/MaintenancePage").then((m) => ({ default: m.MaintenancePage }))
);
const ReferralsPage = lazy(() =>
  import("./pages/ReferralsPage").then((m) => ({ default: m.ReferralsPage }))
);
// Chat widget pulls in socket.io-client - split out of the critical path
const ChatWidget = lazy(() => import("./components/ChatWidget"));

// Defers mounting expensive below-the-fold content until it's actually
// about to be scrolled into view. Specifically added for <FAQSection>:
// Radix UI's Collapsible (used by the accordion) measures every item's
// content height via getBoundingClientRect as soon as it MOUNTS -
// regardless of open/closed state or scroll position - which showed up as
// measurable forced-reflow time during initial page load in a Lighthouse
// audit. Framer Motion's whileInView/useInView doesn't prevent this, since
// it only gates the *animation*, not the DOM mount itself - the Accordion
// was still mounting (and Radix still measuring) immediately either way.
const LazyMount = ({ children, rootMargin = '600px' }) => {
  const ref = useRef(null);
  const [shouldMount, setShouldMount] = useState(false);

  useEffect(() => {
    if (shouldMount) return;
    const el = ref.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setShouldMount(true);
          observer.disconnect();
        }
      },
      { rootMargin }
    );
    observer.observe(el);
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldMount]);

  return <div ref={ref}>{shouldMount ? children : null}</div>;
};

// gtag.js only fires a pageview automatically on the very first load.
// Since this is a client-routed SPA, we need to send one manually on every
// route change too, or every page after the first is invisible to GA.
// (Consent Mode still governs whether this actually gets stored - if the
// visitor hasn't granted analytics consent, gtag drops/models it, it
// doesn't silently bypass their choice.)
const AnalyticsPageviewTracker = () => {
  const location = useLocation();
  useEffect(() => {
    if (typeof window.gtag === "function") {
      window.gtag('event', 'page_view', {
        page_path: location.pathname + location.search,
        page_location: window.location.href,
        page_title: document.title,
      });
    }
  }, [location]);
  return null;
};

const HomePage = () => {
  return (
    <div data-testid="home-page">
      <SEO
        title="Custom Software Development UK | Bespoke Software Company | LOLCorp"
        description="LOLCorp is a UK-based custom software development company building bespoke software, CRMs, dashboards and internal systems from £5,000. Affordable, fixed-scope projects delivered in 4-6 weeks. Get an instant quote."
        path="/"
        jsonLd={faqJsonLd}
      />
      <Navigation />
      <main>
        <HeroSection />
        <AboutSection />
        <ServicesSection />
        <MidPageCta />
        <TestimonialsSection />
        <ValuePropsSection />
        <SupportSection />
        <TechStackSection />
        <LazyMount>
          <FAQSection />
        </LazyMount>
        <ContactSection />
      </main>
      <Footer />
      <Suspense fallback={null}>
        <ChatWidget serverUrl="https://sales.loljs.com" />
      </Suspense>
    </div>
  );
};

function App() {
  useEffect(() => {
    // Initialize Lenis smooth scroll
    const lenis = new Lenis({
      duration: 1.2,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      direction: 'vertical',
      gestureDirection: 'vertical',
      smooth: true,
      smoothTouch: false,
      touchMultiplier: 2,
    });

    function raf(time) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    // Handle hash navigation for smooth scroll
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash) {
        const element = document.querySelector(hash);
        if (element) {
          setTimeout(() => {
            lenis.scrollTo(element, { offset: -80 });
          }, 100);
        }
      }
    };

    window.addEventListener('hashchange', handleHashChange);
    
    // Initial check for hash
    if (window.location.hash) {
      handleHashChange();
    }

    return () => {
      lenis.destroy();
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  return (
    <div className="App">
      {/* Subtle grain texture overlay */}
      <div className="grain-overlay" />
      
      <BrowserRouter>
        <AnalyticsPageviewTracker />
        <Suspense fallback={null}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/consultation" element={<ConsultationPage />} />
            <Route path="/instant-quote" element={<InstantQuotePage />} />
            <Route path="/maintenance" element={<MaintenancePage />} />
            <Route path="/referrals" element={<ReferralsPage />} />
          </Routes>
        </Suspense>
      </BrowserRouter>
      
      <Toaster />
      <CookieConsent />
    </div>
  );
}

export default App;
